import React from 'react';
import Select from 'react-select';

type OptionType = { label: string; value: any };

interface ThemedSelectProps {
  name?: string;
  placeholder?: string;
  value?: any;
  options?: OptionType[];
  onChange?: (value: any) => void;
  isMulti?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  className?: string;
}

/**
 * A React-Select wrapper that reads CSS custom properties from the active
 * theme so the dropdown background, text, borders and option highlights always
 * follow the current surfacePrimary / textPrimary colour scheme.
 */
const ThemedSelect: React.FC<ThemedSelectProps> = (props) => {
  const getCssVar = (name: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  const surfacePrimary   = getCssVar('--color-surface-primary')   || '#ffffff';
  const surfaceSecondary = getCssVar('--color-surface-secondary')  || '#F4F7FE';
  const textPrimary      = getCssVar('--color-text-primary')       || '#1b2559';
  const textSecondary    = getCssVar('--color-text-secondary')     || '#707eae';
  const borderPrimary    = getCssVar('--color-border-primary')     || '#e2e8f0';
  const brand500         = getCssVar('--color-brand-500')          || '#422AFB';

  const themedStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: surfacePrimary,
      borderColor: state.isFocused ? brand500 : borderPrimary,
      boxShadow: state.isFocused ? `0 0 0 1px ${brand500}` : 'none',
      color: textPrimary,
      '&:hover': { borderColor: brand500 },
      minHeight: '48px',
      borderRadius: '12px',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: surfacePrimary,
      border: `1px solid ${borderPrimary}`,
      zIndex: 9999,
    }),
    menuList: (base: any) => ({
      ...base,
      backgroundColor: surfacePrimary,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? brand500
        : state.isFocused
        ? surfaceSecondary
        : surfacePrimary,
      color: state.isSelected ? '#ffffff' : textPrimary,
      '&:active': { backgroundColor: brand500, color: '#ffffff' },
    }),
    singleValue: (base: any) => ({ ...base, color: textPrimary }),
    multiValue: (base: any) => ({ ...base, backgroundColor: surfaceSecondary }),
    multiValueLabel: (base: any) => ({ ...base, color: textPrimary }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: textSecondary,
      '&:hover': { backgroundColor: brand500, color: '#ffffff' },
    }),
    placeholder: (base: any) => ({ ...base, color: textSecondary }),
    input: (base: any) => ({ ...base, color: textPrimary }),
    indicatorSeparator: (base: any) => ({ ...base, backgroundColor: borderPrimary }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: textSecondary,
      '&:hover': { color: brand500 },
    }),
    clearIndicator: (base: any) => ({
      ...base,
      color: textSecondary,
      '&:hover': { color: brand500 },
    }),
    valueContainer: (base: any) => ({
      ...base,
      maxHeight: '50px',
      overflowY: 'auto',
      flexWrap: 'wrap',
      marginTop: 5,
      marginLeft: 10,
    }),
  };

  return (
    <Select
      {...props}
      styles={themedStyles}
    />
  );
};

export default ThemedSelect;
